import { type FC, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useInterval } from 'usehooks-ts';

import { vipBenefitsConfig } from '@/common/config/vip-benefirs';
import { calcSecondsToDate } from '@/common/lib/time';
import { timeStringFormat } from '@/common/lib/timer';
import { ContainerSticker } from '@/components/common/ContainerSticker/ContainerSticker';
import { Ticker } from '@/components/common/Ticker/Ticker';
import { ModalContext } from '@/components/Modals/Modal/config';
import { H3 } from '@/components/Typography/Typography';
import { MainButton } from '@/components/ui/Button/Button';
import { Picture } from '@/components/ui/Picture/Picture';
import { useAppSelector } from '@/redux/store';

import 'swiper/css/pagination';

import { Benefits } from './Benefits/Benefits';

import css from './styles.module.scss';

import boosPng from '/img/characters/happy-boss-char.png';
import boosWebp from '/img/characters/happy-boss-char.webp';
import bgPng from '/img/shop/vip-market-bg.png';
import bgWebp from '/img/shop/vip-market-bg.webp';

const VipMarket: FC = () => {
  const { t } = useTranslation();
  const availableRegistrationOffer = useAppSelector(
    (state) => state.user.vip?.paidVIPCost.availableRegistrationOffer,
  );
  const validUntil = useAppSelector((state) => state.user.vip?.validUntil);
  const [vipSecondsLeft, setVipSecondsLeft] = useState(() =>
    calcSecondsToDate(validUntil),
  );
  useInterval(
    () => setVipSecondsLeft(calcSecondsToDate(validUntil)),
    validUntil ? 1000 : null,
  );
  const { openModal } = useContext(ModalContext);

  const stringTimeToVipEnd = timeStringFormat(vipSecondsLeft * 1000, {
    template: '{days}d : {hours}h : {minutes}m',
  });

  const buyPaidVip = (): void => {
    openModal({
      renderKind: 'selectVipPrice',
      props: { type: 'paid' },
    });
  };

  return (
    <div className="relative w-full">
      <div className="absolute right-0 -top-[8px] z-[4]">
        <Ticker variant="gold" text="vip club" />
      </div>
      <div className={cn(css.vip, availableRegistrationOffer && '!pb-4')}>
        <ContainerSticker
          className="absolute top-0 left-0 -z-[2] [&>span]:!text-sm [&>span]:!leading-none"
          variant={vipSecondsLeft ? 'greyTimerGold' : 'greyInactive'}
          text={vipSecondsLeft ? stringTimeToVipEnd : t('Inactive')}
        />
        <Picture
          className="absolute top-0 right-0 w-auto h-full"
          img={bgPng}
          webp={bgWebp}
          alt="background"
        />
        <Picture
          className="absolute -top-6 right-0 xl:right-[calc(25.7vw_-_23.125rem)] w-[14.75rem] 3xl:right-0 aspect-square"
          img={boosPng}
          webp={boosWebp}
          alt="boss"
        />
        <H3 className="ml-3">{t('Join the VIP Club')}</H3>
        <div className={css.swiperWrapper}>
          <Swiper
            slidesPerView="auto"
            spaceBetween="100%"
            loop={false}
            initialSlide={0}
            pagination={{
              enabled: true,
              bulletClass: 'bg-white/20 size-2 rounded-full',
              bulletActiveClass: '!bg-accent-sec',
            }}
            modules={[Pagination]}
          >
            {Array.from({ length: 7 }).map((_, index) => {
              return (
                <SwiperSlide key={index}>
                  <Benefits
                    icon={vipBenefitsConfig.shopIcons[index]}
                    transKey={vipBenefitsConfig.shopTransKeyObjBenefits[index](
                      t,
                    )}
                    classNames="2xl:[&>p]:text-xs 3xl:[&>p]:text-sm 2xl:[&>p]:!leading-1.2 text-nowrap"
                  />
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
        <div className="relative mt-8 pt-2 flex gap-2">
          <MainButton
            className="h-10 w-full [&_h2]:!text-[1.125rem]"
            text={t('Join now')}
            onClick={buyPaidVip}
          />
          <div className="absolute -right-1 top-1 z-[4]">
            <Ticker
              classnamestext="!text-[0.75rem] !leading-none"
              classnamesinblock="py-0.5"
              variant="gold-small"
              text="-50%"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export { VipMarket };
